/**
 * PHA-983: one place that assembles the input to the Care Plan PDF.
 *
 * The "Download Care Plan" button had accumulated a lot of enrichment over several tickets —
 * the on-demand draft fallback (PHA-895), the prospective APCM tier (PHA-737), the org's
 * no-target-dates flag (PHA-866), care-team reviews (PHA-870), the expiration fallback, and
 * the is_finalized_plan gate (PHA-942). "Push Care Plan to EMR" renders the same document, so if it
 * built its own payload the copy filed into the medical record would quietly drift from the
 * copy clinicians print — the kind of divergence nobody notices until it matters.
 *
 * Both callers go through here instead.
 */
import { patientApi } from '../services/api';
import { maskedDateToIso, defaultExpirationMasked, DEFAULT_CARE_PLAN_REVIEW_MONTHS } from './dateInputMask';

// The PDF util's PatientData is structurally loose (several fields bridge from draft shapes),
// so the payload is typed as a record rather than re-declaring it here.
/* eslint-disable @typescript-eslint/no-explicit-any */

export interface CarePlanPdfPayloadOptions {
  /** The patient record as loaded by the chart. */
  patient: any;
  /** Approved care plan definitions, if any. */
  carePlanDefinitions?: any[] | null;
  /** Persisted draft definitions, used when there is no approved plan. */
  draftCarePlanDefinitions?: any[] | null;
  /** Whether the patient has at least one approved plan. */
  hasApprovedCarePlans: boolean;
  /** Org flags, resolved by the chart. */
  voiceOfPatientEnabled?: boolean;
  noTargetDatesEnabled?: boolean;
  /**
   * PHA-1080: the clinic's care plan review cycle in months, resolved by the chart
   * (useCarePlanReviewMonths). Used ONLY for the draft fallback below -- an approved
   * plan carries a stored expiration_date and never reaches it. Defaults to
   * DEFAULT_CARE_PLAN_REVIEW_MONTHS so a caller that has no org context still gets
   * the pre-PHA-1080 behaviour rather than NaN.
   */
  carePlanReviewMonths?: number;
  /**
   * PHA-1285: the review date the clinician set on this draft (ISO yyyy-MM-dd, from
   * care_plan_draft_meta). Wins over the fallback below, since approval will store it.
   */
  draftReviewDate?: string | null;
  /** PHA-870 care-team review records. */
  carePlanReviews?: any[];
  /**
   * PHA-983: true when this render is destined for the EHR. Suppresses the
   * "Printed and handed to the patient" note, which must not appear on a filed copy.
   */
  isEhrCopy?: boolean;
  /**
   * PHA-1032: current logged-in user's display name. Shown as "Generated and reviewed by"
   * on a draft render — there is no approving clinician until approval.
   */
  draftReviewerName?: string | null;
}

/**
 * PHA-1290: the patient's Care Plan Notes (PHA-870 review records), fetched at print time.
 *
 * Every print button used to read a list loaded somewhere else: the chart only loads it when
 * the Care Plans tab opens, but its Download / Print Draft buttons sit in the header on every
 * tab, and the approval screen's Print Draft passed a hardcoded []. Notes added before the
 * plan was approved — or a chart opened on another tab — printed without them.
 *
 * A failed fetch falls back to `fallback` (whatever the caller already has on screen) rather
 * than [] so a transient error doesn't blank notes that were already loaded (PHA-951).
 */
export const fetchCarePlanReviewsForPdf = async (
  patientId: string | null | undefined,
  fallback: any[] = []
): Promise<any[]> => {
  if (!patientId) return fallback;
  try {
    const result = await patientApi.getCarePlanReviews(patientId);
    return Array.isArray(result?.reviews) ? result.reviews : fallback;
  } catch (err) {
    console.error('PHA-1290: care plan notes fetch for PDF failed; using the notes already loaded:', err);
    return fallback;
  }
};

/**
 * Build the PDF payload, fetching the prospective APCM tier when a draft is being rendered.
 *
 * The tier fetch is awaited (rather than pre-loaded) for the reason PHA-737 documented: it
 * avoids the race where the button is pressed before the value resolves. A failure falls
 * back to the persisted tier rather than blocking the document.
 */
export const buildCarePlanPdfPayload = async (
  options: CarePlanPdfPayloadOptions
): Promise<any> => {
  const {
    patient,
    carePlanDefinitions,
    draftCarePlanDefinitions,
    hasApprovedCarePlans,
    voiceOfPatientEnabled,
    noTargetDatesEnabled,
    carePlanReviews,
    isEhrCopy,
    draftReviewerName,
    carePlanReviewMonths = DEFAULT_CARE_PLAN_REVIEW_MONTHS,
    draftReviewDate = null,
  } = options;

  const useDraft = !hasApprovedCarePlans && (draftCarePlanDefinitions?.length ?? 0) > 0;

  // PHA-1032: a draft must never reach the EHR — "Push Care Plan to EMR" only renders once an
  // approved plan exists, so this should be unreachable; fail loudly if that ever changes.
  if (useDraft && isEhrCopy === true) {
    throw new Error('PHA-1032: refusing to build an EHR-copy payload for a draft care plan');
  }

  const base: any = {
    ...patient,
    voice_of_patient_enabled: voiceOfPatientEnabled,
    no_target_dates_enabled: noTargetDatesEnabled,
    care_plan_reviews: carePlanReviews ?? [],
    is_ehr_copy: isEhrCopy === true,
    is_draft: useDraft,
    draft_reviewer_name: useDraft ? (draftReviewerName ?? null) : null,
  };

  if (!useDraft) {
    return {
      ...base,
      care_plan_definitions: carePlanDefinitions,
      // PHA-942: the "printed and handed" note is for FINALIZED plans only. This branch is
      // also reached with zero approved AND zero draft plans, so gate on the flag rather
      // than on the branch.
      is_finalized_plan: hasApprovedCarePlans,
    };
  }

  // Draft render: patients.apcm_tier reflects the LAST approval (and is null before the
  // first), so the persisted value would label a draft with the wrong program. Compute what
  // approval WOULD set.
  let prospectiveApcmTier = patient.apcm_tier ?? null;
  try {
    prospectiveApcmTier = await patientApi.getDraftApcmTier(patient.patient_id);
  } catch (err) {
    console.error('PHA-983: prospective APCM tier fetch failed; using persisted value:', err);
  }

  return {
    ...base,
    apcm_tier: prospectiveApcmTier,
    // Drafts carry no stored expiration; reuse an existing approved plan's date if there is
    // one, else the same today+cycle default the backend applies on approve. PHA-1080: the
    // cycle is the clinic's configured one, so the projected date on a printed draft matches
    // what approval will actually store.
    expiration_date:
      draftReviewDate ??
      carePlanDefinitions?.find((d: any) => d.expiration_date)?.expiration_date ??
      maskedDateToIso(defaultExpirationMasked(carePlanReviewMonths)),
    care_plan_definitions: draftCarePlanDefinitions,
  };
};
